/**
 * WSClient
 *
 * Transport-level glue between the edge Worker's Durable Object and the
 * in-process Bus. Responsibilities:
 *   - Connect to a configurable URL.
 *   - Send a typed subscribe frame on open (with optional lastEventId resume).
 *   - Parse JSON envelopes, validate channel discriminator, dispatch into Bus.
 *   - Exponential backoff with jitter on disconnect.
 *   - Heartbeat ping/pong to detect half-open connections.
 *   - Expose observable connection state.
 */

import type { Bus } from '@tiw/event-bus';
import type { ChannelName, EventEnvelope, EventMap } from '@tiw/shared-types';

export type WSConnectionState =
  | 'idle'
  | 'connecting'
  | 'open'
  | 'closing'
  | 'closed'
  | 'reconnecting';

export interface WSSubscribeFrame {
  readonly type: 'subscribe';
  readonly channels: ReadonlyArray<ChannelName>;
  readonly lastEventId?: Record<string, string>;
}

export interface WSResumeOptions {
  /** Per-channel last event id observed by this client. Sent on (re)connect. */
  readonly lastEventId?: Record<string, string>;
}

export interface WSClientOptions {
  readonly url: string;
  readonly channels: ReadonlyArray<ChannelName>;
  readonly bus: Bus<EventMap>;
  /** Heartbeat ping interval in ms. Default 15000. */
  readonly heartbeatMs?: number;
  /** Initial backoff in ms. Default 500. */
  readonly initialBackoffMs?: number;
  /** Max backoff in ms. Default 15000. */
  readonly maxBackoffMs?: number;
  /** Inject a WebSocket factory for testing. Defaults to globalThis.WebSocket. */
  readonly webSocketFactory?: (url: string) => WebSocket;
  /** Called whenever the connection state changes. */
  readonly onStateChange?: (state: WSConnectionState) => void;
  /** Called on decode/validate errors; envelope passed if it parsed at all. */
  readonly onError?: (err: Error, raw?: string) => void;
}

const VALID_CHANNELS = new Set<ChannelName>([
  'tiw.topology.update',
  'tiw.flow.tick',
  'tiw.incident.open',
  'tiw.incident.close',
  'tiw.bgp.withdraw',
  'tiw.dns.spike',
]);

function isChannelName(s: unknown): s is ChannelName {
  return typeof s === 'string' && VALID_CHANNELS.has(s as ChannelName);
}

function isEventEnvelope(value: unknown): value is EventEnvelope {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    isChannelName(v.channel) &&
    typeof v.ts === 'string' &&
    typeof v.id === 'string' &&
    typeof v.source === 'string' &&
    typeof v.payload === 'object' &&
    v.payload !== null
  );
}

export class WSClient {
  private socket: WebSocket | null = null;
  private state: WSConnectionState = 'idle';
  private attempt = 0;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionallyClosed = false;
  private readonly lastEventId: Record<string, string> = {};

  constructor(private readonly options: WSClientOptions) {}

  getState(): WSConnectionState {
    return this.state;
  }

  /**
   * Open the connection. Safe to call repeatedly; no-ops if already open or
   * mid-handshake.
   */
  connect(resume?: WSResumeOptions): void {
    if (this.state === 'open' || this.state === 'connecting') return;
    this.intentionallyClosed = false;
    if (resume?.lastEventId) Object.assign(this.lastEventId, resume.lastEventId);
    this.openSocket();
  }

  /** Hard close. Disables reconnect until connect() is called again. */
  close(code = 1000, reason = 'client_close'): void {
    this.intentionallyClosed = true;
    this.clearTimers();
    this.setState('closing');
    try {
      this.socket?.close(code, reason);
    } catch {
      // Ignore; transitioning to closed via handler.
    }
  }

  /** Send a raw subscribe frame. Useful for adding channels mid-session. */
  subscribe(channels: ReadonlyArray<ChannelName>): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    const hasResume = Object.keys(this.lastEventId).length > 0;
    const frame: WSSubscribeFrame = hasResume
      ? { type: 'subscribe', channels, lastEventId: this.lastEventId }
      : { type: 'subscribe', channels };
    this.socket.send(JSON.stringify(frame));
  }

  private openSocket(): void {
    this.setState(this.attempt === 0 ? 'connecting' : 'reconnecting');
    const factory = this.options.webSocketFactory ?? ((u: string) => new WebSocket(u));
    let ws: WebSocket;
    try {
      ws = factory(this.options.url);
    } catch (err) {
      this.options.onError?.(err instanceof Error ? err : new Error(String(err)));
      this.scheduleReconnect();
      return;
    }
    this.socket = ws;
    // The hub sends JSON **text** frames. We still set arraybuffer + decode it
    // defensively below, so a future binary transport wouldn't break the client.
    ws.binaryType = 'arraybuffer';

    ws.onopen = (): void => {
      this.attempt = 0;
      this.setState('open');
      this.subscribe(this.options.channels);
      this.startHeartbeat();
    };

    ws.onmessage = (ev: MessageEvent): void => {
      const { data } = ev;
      const raw =
        typeof data === 'string'
          ? data
          : data instanceof ArrayBuffer
            ? new TextDecoder().decode(data)
            : '';
      this.handleMessage(raw);
    };

    ws.onerror = (): void => {
      this.options.onError?.(new Error('WebSocket error'));
    };

    ws.onclose = (): void => {
      this.stopHeartbeat();
      this.socket = null;
      if (this.intentionallyClosed) {
        this.setState('closed');
        return;
      }
      this.scheduleReconnect();
    };
  }

  private handleMessage(raw: string): void {
    if (raw === '' || raw === 'pong') return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch (err) {
      this.options.onError?.(
        err instanceof Error ? err : new Error('Invalid JSON'),
        raw,
      );
      return;
    }
    // Control frames (hello, ping) carry a `type` field instead of `channel`.
    // They are not events; skip envelope validation. See the hub in
    // apps/edge/src/hub.ts.
    if (typeof parsed === 'object' && parsed !== null && 'type' in parsed) {
      return;
    }
    if (!isEventEnvelope(parsed)) {
      this.options.onError?.(new Error('Envelope failed validation'), raw);
      return;
    }
    this.lastEventId[parsed.channel] = parsed.id;
    // The discriminated union is sound at runtime because we validated `channel`.
    this.options.bus.emit(parsed.channel, parsed as EventMap[typeof parsed.channel]);
  }

  private startHeartbeat(): void {
    const interval = this.options.heartbeatMs ?? 15000;
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        try {
          this.socket.send('ping');
        } catch {
          // Next onclose handles recovery.
        }
      }
    }, interval);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect(): void {
    this.attempt += 1;
    const base = this.options.initialBackoffMs ?? 500;
    const max = this.options.maxBackoffMs ?? 15000;
    const expo = Math.min(max, base * 2 ** Math.min(10, this.attempt - 1));
    const jitter = expo * 0.3 * Math.random();
    const delay = Math.floor(expo + jitter);
    this.setState('reconnecting');
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket();
    }, delay);
  }

  private clearTimers(): void {
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private setState(s: WSConnectionState): void {
    if (this.state === s) return;
    this.state = s;
    this.options.onStateChange?.(s);
  }
}
